import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeftIcon,
    ArrowPathIcon,
    ArrowTopRightOnSquareIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationCircleIcon,
    IdentificationIcon,
    ShieldCheckIcon,
    UserCircleIcon,
    XCircleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { extractApiError } from "@/lib/api-error";

import {
    getDriverLicenseVerification,
    requeueDriverLicenseVerification,
    reviewDriverLicenseVerification,
} from "../api/driverLicenceApi";
import type {
    CrossCheckDetails,
    CrossCheckEntry,
    DriverLicenseReviewAction,
    DriverLicenseVerificationDetails,
    LicenseVerificationStatus,
} from "../types";

const STATUS_META: Record<
    LicenseVerificationStatus,
    { label: string; tone: string }
> = {
    SUBMITTED: {
        label: "Submitted",
        tone: "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300",
    },
    OCR_PROCESSING: {
        label: "OCR Processing",
        tone: "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/50 dark:bg-sky-950/30 dark:text-sky-300",
    },
    OCR_FAILED: {
        label: "OCR Failed",
        tone: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300",
    },
    CROSS_CHECK_FAILED: {
        label: "Cross-check Failed",
        tone: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300",
    },
    PROVISIONALLY_APPROVED: {
        label: "Provisionally Approved",
        tone: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300",
    },
    PENDING_BULK_VALIDATION: {
        label: "Pending Provider Validation",
        tone: "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/50 dark:bg-violet-950/30 dark:text-violet-300",
    },
    FULLY_VERIFIED: {
        label: "Fully Verified",
        tone: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-300",
    },
    SUSPENDED: {
        label: "Suspended",
        tone: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300",
    },
};

const CROSS_CHECK_LABELS: Record<string, string> = {
    formatValid: "License format valid",
    issueDateInPast: "Issue date is in the past",
    notExpired: "License is not expired",
    expiryAfterIssue: "Expiry is after issue date",
    noDuplicateLicense: "No duplicate license found",
    ocrImageQuality: "OCR image quality acceptable",
    licenseNumberFoundInImage: "License number found in image",
    licenseNumberOnImageMatchesSubmitted: "License number matches submission",
    issueDateOnImageMatchesSubmitted: "Issue date matches submission",
    expiryDateOnImageMatchesSubmitted: "Expiry date matches submission",
    nameMatchesProfile: "Name matches driver profile",
    dobMatchesProfile: "Date of birth matches profile",
    genderMatchesProfile: "Gender matches profile",
};

const formatDateTime = (value: string | null | undefined) => {
    if (!value) return "—";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat("en-NG", {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(date);
};

const formatDateOnly = (value: string | null | undefined) => {
    if (!value) return "—";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat("en-NG", {
        dateStyle: "medium",
    }).format(date);
};

const formatRelativeTime = (value: string | null | undefined) => {
    if (!value) return "—";

    const timestamp = new Date(value).getTime();
    if (Number.isNaN(timestamp)) return value;

    const diffMs = timestamp - Date.now();
    const absoluteMinutes = Math.round(Math.abs(diffMs) / 60_000);

    if (absoluteMinutes < 1) return "Now";
    if (absoluteMinutes < 60) {
        const unit = absoluteMinutes === 1 ? "minute" : "minutes";
        return diffMs >= 0
            ? `In ${absoluteMinutes} ${unit}`
            : `${absoluteMinutes} ${unit} ago`;
    }

    const absoluteHours = Math.round(absoluteMinutes / 60);
    if (absoluteHours < 24) {
        const unit = absoluteHours === 1 ? "hour" : "hours";
        return diffMs >= 0
            ? `In ${absoluteHours} ${unit}`
            : `${absoluteHours} ${unit} ago`;
    }

    const absoluteDays = Math.round(absoluteHours / 24);
    const unit = absoluteDays === 1 ? "day" : "days";
    return diffMs >= 0 ? `In ${absoluteDays} ${unit}` : `${absoluteDays} ${unit} ago`;
};

const buildDriverName = (record: DriverLicenseVerificationDetails) => {
    const fullName = [record.firstName, record.lastName].filter(Boolean).join(" ").trim();
    return fullName || "Unknown driver";
};

const isImageDocument = (url: string | null | undefined) =>
    Boolean(url && /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(url));

const normalizeCrossChecks = (
    input: DriverLicenseVerificationDetails["crossCheckDetails"]
) => {
    if (!input) return [] as Array<[string, CrossCheckEntry]>;

    let parsed: CrossCheckDetails | null = null;

    if (typeof input === "string") {
        try {
            parsed = JSON.parse(input) as CrossCheckDetails;
        } catch {
            return [] as Array<[string, CrossCheckEntry]>;
        }
    } else {
        parsed = input;
    }

    return Object.entries(parsed).filter(
        (entry): entry is [string, CrossCheckEntry] => Boolean(entry[1])
    );
};

const DetailCard = ({
    title,
    subtitle,
    children,
}: {
    title: string;
    subtitle?: string;
    children: React.ReactNode;
}) => (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="border-b border-slate-200 px-6 py-4 dark:border-slate-800">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {title}
            </h2>
            {subtitle ? (
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
            ) : null}
        </div>
        <div className="px-6 py-5">{children}</div>
    </section>
);

const InfoGrid = ({
    rows,
}: {
    rows: Array<{ label: string; value: React.ReactNode }>;
}) => (
    <div className="grid gap-4 sm:grid-cols-2">
        {rows.map((row) => (
            <div key={row.label} className="rounded-xl bg-slate-50 p-4 dark:bg-slate-950/40">
                <p className="text-xs uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                    {row.label}
                </p>
                <div className="mt-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                    {row.value || "—"}
                </div>
            </div>
        ))}
    </div>
);

export const DriverLicenceDetailsRoute = () => {
    const { verificationId } = useParams<{ verificationId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasPermission } = usePermissions();
    const canManageApprovals = hasPermission(SECTION_PERMISSIONS.APPROVALS_MANAGE[0]);

    const [reason, setReason] = useState("");
    const [feedback, setFeedback] = useState<{
        tone: "success" | "error";
        text: string;
    } | null>(null);

    useEffect(() => {
        if (!feedback) return undefined;

        const timeoutId = window.setTimeout(() => setFeedback(null), 6000);
        return () => window.clearTimeout(timeoutId);
    }, [feedback]);

    const { data, isLoading, error, isFetching, refetch } = useQuery({
        queryKey: ["driver-license-verification", verificationId],
        queryFn: () => getDriverLicenseVerification(verificationId!),
        enabled: Boolean(verificationId),
    });

    const record = data?.data;

    const invalidateRelatedQueries = async (driverId: string) => {
        await queryClient.invalidateQueries({
            queryKey: ["driver-license-verifications"],
        });
        await queryClient.invalidateQueries({
            queryKey: ["driver-license-verification", verificationId],
        });
        await queryClient.invalidateQueries({
            queryKey: ["driverDetails", driverId],
        });
        await queryClient.invalidateQueries({
            queryKey: ["drivers"],
        });
    };

    const reviewMutation = useMutation({
        mutationFn: ({
            action,
            note,
        }: {
            action: Exclude<DriverLicenseReviewAction, "REQUEUE">;
            note?: string;
        }) =>
            reviewDriverLicenseVerification(verificationId!, {
                action,
                reason: note,
            }),
        onSuccess: async (response) => {
            if (record) {
                await invalidateRelatedQueries(record.driverId);
            }
            setFeedback({
                tone: "success",
                text: response.message || response.data.message || "Review action completed.",
            });
            setReason("");
        },
        onError: (mutationError) => {
            setFeedback({
                tone: "error",
                text: extractApiError(mutationError),
            });
        },
    });

    const requeueMutation = useMutation({
        mutationFn: (note?: string) =>
            requeueDriverLicenseVerification(verificationId!, {
                reason: note,
            }),
        onSuccess: async (response) => {
            if (record) {
                await invalidateRelatedQueries(record.driverId);
            }
            setFeedback({
                tone: "success",
                text: response.message || response.data.message || "Verification requeued.",
            });
            setReason("");
        },
        onError: (mutationError) => {
            setFeedback({
                tone: "error",
                text: extractApiError(mutationError),
            });
        },
    });

    const crossChecks = useMemo(
        () => normalizeCrossChecks(record?.crossCheckDetails ?? null),
        [record?.crossCheckDetails]
    );

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-24">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error || !record) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-6 py-8 text-center dark:border-rose-900/60 dark:bg-rose-900/20">
                <ExclamationCircleIcon className="mx-auto h-10 w-10 text-rose-500" />
                <p className="mt-4 text-sm text-rose-700 dark:text-rose-300">
                    {extractApiError(error ?? new Error("Verification record not found"))}
                </p>
                <Button
                    variant="outline"
                    className="mt-5"
                    onClick={() => navigate("/approvals/driver-licences")}
                >
                    Back to queue
                </Button>
            </div>
        );
    }

    const statusMeta = STATUS_META[record.status];
    const isFinalStatus =
        record.status === "FULLY_VERIFIED" || record.status === "SUSPENDED";
    const canRequeue =
        canManageApprovals && record.status === "PENDING_BULK_VALIDATION";
    const canTakeFinalAction = canManageApprovals && !isFinalStatus;

    return (
        <div className="space-y-6">
            <button
                type="button"
                onClick={() => navigate("/approvals/driver-licences")}
                className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
                <ArrowLeftIcon className="h-4 w-4" />
                Back to driver licence queue
            </button>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                    <div className="space-y-4">
                        <div className="flex items-center gap-4">
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 shadow-lg">
                                <IdentificationIcon className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                                    {record.submittedLicenseNumber || "Unlabelled licence record"}
                                </h1>
                                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                    {buildDriverName(record)}
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            <span
                                className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusMeta.tone}`}
                            >
                                {statusMeta.label}
                            </span>
                            <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300">
                                Attempt #{record.attemptNumber}
                            </span>
                            {record.provider ? (
                                <span className="inline-flex rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300">
                                    Provider: {record.provider}
                                </span>
                            ) : null}
                        </div>

                        <p className="max-w-3xl text-sm text-slate-500 dark:text-slate-400">
                            This record is part of the admin-managed licence verification flow.
                            Use this screen to inspect local checks, provider state, and make final
                            operational decisions when policy allows.
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <Link to={`/drivers/${record.driverId}`}>
                            <Button variant="outline">
                                <UserCircleIcon className="mr-2 h-4 w-4" />
                                View driver profile
                            </Button>
                        </Link>
                        <Button variant="outline" onClick={() => refetch()}>
                            <ArrowPathIcon
                                className={`mr-2 h-4 w-4 ${
                                    isFetching ? "animate-spin" : ""
                                }`}
                            />
                            Refresh
                        </Button>
                    </div>
                </div>
            </section>

            {feedback ? (
                <div
                    className={`rounded-2xl px-5 py-4 text-sm ${
                        feedback.tone === "success"
                            ? "border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-900/20 dark:text-emerald-300"
                            : "border border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/60 dark:bg-rose-900/20 dark:text-rose-300"
                    }`}
                >
                    {feedback.text}
                </div>
            ) : null}

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1.5fr)_minmax(320px,0.9fr)]">
                <div className="space-y-6">
                    <DetailCard
                        title="Driver and submission"
                        subtitle="Core identity and document metadata used during the verification flow."
                    >
                        <InfoGrid
                            rows={[
                                {
                                    label: "Driver",
                                    value: (
                                        <div>
                                            <p>{buildDriverName(record)}</p>
                                            <p className="mt-1 text-xs font-normal text-slate-500 dark:text-slate-400">
                                                {record.email || "No email"} ·{" "}
                                                {record.phoneNumber || "No phone"}
                                            </p>
                                        </div>
                                    ),
                                },
                                {
                                    label: "Driver ID",
                                    value: (
                                        <span className="font-mono text-xs">
                                            {record.driverId}
                                        </span>
                                    ),
                                },
                                {
                                    label: "Submitted issue date",
                                    value: formatDateOnly(record.submittedIssueDate),
                                },
                                {
                                    label: "Submitted expiry date",
                                    value: formatDateOnly(record.submittedExpiryDate),
                                },
                                {
                                    label: "Submitted at",
                                    value: formatDateTime(record.submittedAt),
                                },
                                {
                                    label: "Last updated",
                                    value: formatDateTime(record.updatedAt),
                                },
                            ]}
                        />

                        <div className="mt-5 flex flex-wrap items-center gap-3">
                            {record.documentUrl ? (
                                <a
                                    href={record.documentUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400"
                                >
                                    Open uploaded document
                                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                                </a>
                            ) : (
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    No uploaded document URL available.
                                </p>
                            )}
                        </div>

                        {isImageDocument(record.documentUrl) ? (
                            <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40">
                                <img
                                    src={record.documentUrl ?? ""}
                                    alt="Driver licence upload"
                                    className="max-h-[420px] w-full object-contain"
                                />
                            </div>
                        ) : null}
                    </DetailCard>

                    <DetailCard
                        title="Local verification breakdown"
                        subtitle="Use cross-check details to understand why local checks failed before escalating or overriding."
                    >
                        {record.rejectionReason ? (
                            <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-200">
                                <p className="font-semibold">Rejection reason</p>
                                <p className="mt-1">{record.rejectionReason}</p>
                            </div>
                        ) : null}

                        {crossChecks.length === 0 ? (
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                No structured cross-check details were returned for this record.
                            </p>
                        ) : (
                            <div className="grid gap-3">
                                {crossChecks.map(([key, entry]) => (
                                    <div
                                        key={key}
                                        className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950/40"
                                    >
                                        {entry.passed ? (
                                            <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
                                        ) : (
                                            <XCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-rose-500" />
                                        )}
                                        <div>
                                            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                {CROSS_CHECK_LABELS[key] || key}
                                            </p>
                                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                                {entry.detail || (entry.passed ? "Passed" : "Failed")}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </DetailCard>
                </div>

                <div className="space-y-6">
                    <DetailCard
                        title="Provider stage"
                        subtitle="Current state of backend-managed provider validation and retry handling."
                    >
                        <InfoGrid
                            rows={[
                                {
                                    label: "Provider result",
                                    value: record.providerResult || "No provider result yet",
                                },
                                {
                                    label: "Provider reference",
                                    value: record.providerReference || "—",
                                },
                                {
                                    label: "Provider attempts",
                                    value: record.providerAttemptCount ?? 0,
                                },
                                {
                                    label: "Last provider attempt",
                                    value: (
                                        <div>
                                            <p>{formatDateTime(record.providerLastAttemptAt)}</p>
                                            <p className="mt-1 text-xs font-normal text-slate-500 dark:text-slate-400">
                                                {formatRelativeTime(record.providerLastAttemptAt)}
                                            </p>
                                        </div>
                                    ),
                                },
                                {
                                    label: "Next retry",
                                    value: (
                                        <div>
                                            <p>{formatDateTime(record.nextRetryAt)}</p>
                                            <p className="mt-1 text-xs font-normal text-slate-500 dark:text-slate-400">
                                                {formatRelativeTime(record.nextRetryAt)}
                                            </p>
                                        </div>
                                    ),
                                },
                                {
                                    label: "Provider error",
                                    value: record.providerLastError || "No provider error captured",
                                },
                            ]}
                        />
                    </DetailCard>

                    <DetailCard
                        title="Timeline"
                        subtitle="Important timestamps for queue handling and final resolution."
                    >
                        <div className="space-y-4">
                            {[
                                {
                                    label: "Submitted",
                                    value: record.submittedAt,
                                },
                                {
                                    label: "Provisionally approved",
                                    value: record.provisionallyApprovedAt,
                                },
                                {
                                    label: "Fully verified",
                                    value: record.fullyVerifiedAt,
                                },
                                {
                                    label: "Suspended",
                                    value: record.suspendedAt,
                                },
                            ].map((item) => (
                                <div key={item.label} className="flex items-start gap-3">
                                    <ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                                    <div>
                                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                            {item.label}
                                        </p>
                                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                            {formatDateTime(item.value)}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </DetailCard>

                    <DetailCard
                        title="Manual review actions"
                        subtitle="Use trusted operations judgement before applying a final decision."
                    >
                        {!canManageApprovals ? (
                            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-200">
                                You have view access to this queue, but not the manage permission
                                needed to requeue, approve, or suspend a record.
                            </div>
                        ) : null}

                        <label className="block text-sm">
                            <span className="font-medium text-slate-700 dark:text-slate-200">
                                Review note
                            </span>
                            <textarea
                                rows={4}
                                value={reason}
                                onChange={(event) => setReason(event.target.value)}
                                placeholder="Add operational context, evidence reviewed, or provider failure notes."
                                className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                            />
                        </label>

                        <div className="mt-5 space-y-3">
                            <Button
                                className="w-full justify-center"
                                variant="outline"
                                onClick={() => requeueMutation.mutate(reason.trim() || undefined)}
                                disabled={!canRequeue || requeueMutation.isPending}
                            >
                                {requeueMutation.isPending ? "Requeueing..." : "Requeue provider validation"}
                            </Button>
                            <Button
                                className="w-full justify-center bg-emerald-600 hover:bg-emerald-700 focus-visible:ring-emerald-400"
                                onClick={() =>
                                    reviewMutation.mutate({
                                        action: "APPROVE",
                                        note: reason.trim() || undefined,
                                    })
                                }
                                disabled={!canTakeFinalAction || reviewMutation.isPending}
                            >
                                {reviewMutation.isPending
                                    ? "Saving decision..."
                                    : "Approve and fully verify"}
                            </Button>
                            <Button
                                className="w-full justify-center bg-rose-600 hover:bg-rose-700 focus-visible:ring-rose-400"
                                onClick={() =>
                                    reviewMutation.mutate({
                                        action: "SUSPEND",
                                        note: reason.trim() || undefined,
                                    })
                                }
                                disabled={!canTakeFinalAction || reviewMutation.isPending}
                            >
                                {reviewMutation.isPending
                                    ? "Saving decision..."
                                    : "Suspend driver"}
                            </Button>
                        </div>
                    </DetailCard>
                </div>
            </div>
        </div>
    );
};
